import * as React from 'react';
import {IconLoading} from 'icons';

export interface UseLoadingReturn<T> {
  data: T | undefined,
  setData: (data: T | undefined) => void,
  isLoaded: boolean,
  setLoaded: (loaded: boolean) => void,
  fetch: (callback: () => Promise<T>) => void,
  render: (
    content: () => JSX.Element,
    notFound: () => JSX.Element,
    loading?: () => JSX.Element,
  ) => JSX.Element
}

/**
 * This hook helps you handle the loading state.
 * @return {UseLoadingReturn<T>}
 */
export default function useLoading<T extends any>(): UseLoadingReturn<T> {
  const [
    isLoaded, setLoaded,
  ] = React.useState<boolean>(false);

  const [
    data, setData,
  ] = React.useState<T | undefined>(undefined);

  /**
   * Set loaded state.
   */
  function loaded(): void {
    setLoaded(true);
  }

  /**
   * Set unloaded state.
   */
  function unloaded(): void {
    setLoaded(false);
  }

  /**
   * Fetch the data.
   * @param {Promise<T>} callback
   * @return {void}
   */
  function fetch(callback: (data?: T) => Promise<T>): void {
    unloaded();
    callback(data).then((response) => {
      setData(response);
      loaded();
    });
  }

  /**
   * Render.
   * @param {JSX.Element} content
   * @param {JSX.Element} notFound
   * @param {JSX.Element} loading
   * @return {JSX.Element}
   */
  function render(
      content: () => JSX.Element,
      notFound: () => JSX.Element,
      loading?: () => JSX.Element,
  ) {
    /**
     * Render not found.
     * @return {JSX.Element | undefined}
     */
    function renderNotFound(): JSX.Element | undefined {
      return notFound ? notFound() : undefined;
    }

    /**
     * Render loading.
     * @return {JSX.Element}
     */
    function renderLoading(): JSX.Element {
      return loading ? loading() : (
        <IconLoading />
      );
    }

    return (
      <React.Fragment>
        {isLoaded ? (
          (
            data !== undefined
          ) ? content() : renderNotFound()
        ) : renderLoading()}
      </React.Fragment>
    );
  }

  return {
    isLoaded,
    setLoaded,
    data,
    setData,
    fetch,
    render,
  };
}
